import type { FC } from 'react';
import React, { useCallback, useId, useState } from 'react';

import { Checkbox } from '@motorway/mw-highway-code/alpha';

import { DropdownPanel } from '../DropdownPanel';
import { ALL_OPTION_VALUE } from '../FilterBarV2.consts';

import styles from './CheckboxFilterDropdown.module.scss';

interface CheckboxFilterDropdownProps {
	onApply: (selected: string[]) => void;
	onClear: () => void;
	onClose?: () => void;
	options: { label: string; value: string }[];
	selected: string[];
	title?: string;
}

export const CheckboxFilterDropdown: FC<CheckboxFilterDropdownProps> = ({
	onApply,
	onClear,
	onClose,
	options,
	selected,
	title,
}) => {
	const [localSelected, setLocalSelected] = useState<string[]>(selected);

	const hasAllOption = options.some((o) => o.value === ALL_OPTION_VALUE);
	const isAllSelected = hasAllOption && localSelected.length === 0;
	const idPrefix = useId();

	const handleToggle = useCallback((value: string) => {
		if (value === ALL_OPTION_VALUE) {
			setLocalSelected([]);
			return;
		}
		setLocalSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
	}, []);

	const handleApply = useCallback(() => {
		onApply(localSelected);
		onClose?.();
	}, [localSelected, onApply, onClose]);

	const handleClear = useCallback(() => {
		setLocalSelected([]);
		onClear();
		onClose?.();
	}, [onClear, onClose]);

	const hasChanged = localSelected.length !== selected.length || localSelected.some((v) => !selected.includes(v));
	const hasStoreValue = selected.length > 0;

	return (
		<DropdownPanel
			clearDisabled={localSelected.length === 0}
			hasChanged={hasChanged}
			hasStoreValue={hasStoreValue}
			onApply={handleApply}
			onClear={handleClear}
		>
			{title && <span className={styles.dropdownTitle}>{title}</span>}
			<div className={styles.checkboxList}>
				{options.map((option) => {
					const isAll = option.value === ALL_OPTION_VALUE;

					return (
						<Checkbox
							key={isAll ? 'all' : option.value}
							formProps={{
								input: {
									checked: isAll ? isAllSelected : localSelected.includes(option.value),
									onChange: () => handleToggle(option.value),
								},
							}}
							id={`${idPrefix}-${isAll ? 'all' : option.value}`}
							label={option.label}
						/>
					);
				})}
			</div>
		</DropdownPanel>
	);
};
